import React, { useState } from 'react';
import { useLatest, useRequest } from 'ahooks';

/**
 * @typedef {import('../../../../types/provider').Provider} Provider
 */

import { fetchFirstProviderByNpi } from '../../../../utils/serviceCalls/provider';
import { isValidNpi } from '../../../../utils/validation';
import {
  ClearIcon,
  LoadingIndicator,
  SearchSubmitIcon,
} from '../../../Shared/SearchCombobox/Icons';
import { VALIDATION_MESSAGES } from '../../constants';
import { FIELDS, INPUT_WITH_ICON_PADDING_CLASSES } from '../constants';
import FieldWrapper from './FieldWrapper';
import { useFormContextSafe, useFormField } from './hooks';

/**
 * Hook to search for provider by NPI.
 *
 * Uses the ref pattern (like Last4Field) so validation can check the API error
 * directly, without needing an Effect to sync errors to form state.
 *
 * Calls onProviderUpdate directly when search completes (not via Effect).
 *
 * @param {string|null} npi - 10-digit NPI number
 * @param {boolean} disabled - Whether search should be skipped
 * @param {(provider: Provider|null) => void} onProviderUpdate - Callback when provider changes
 * @returns {{ busy: boolean, dirty:boolean, provider: Provider|null, apiErrorRef: React.RefObject<string|null> }}
 */
function useNpiSearch(npi, disabled = false, onProviderUpdate) {
  const { trigger } = useFormContextSafe();

  const [apiErrorMessage, setApiErrorMessage] = useState(
    /** @type {string|null} */ (null),
  );
  const [lastSearchedNpi, setLastSearchedNpi] = useState(npi || '');

  const dirty = npi !== lastSearchedNpi;
  const shouldSearch = !disabled && !!npi;

  const { loading, data: provider } = useRequest(
    async () => {
      if (!npi || disabled) {
        return null;
      }

      try {
        const { provider: foundProvider, errorCode: fetchErrorCode } =
          await fetchFirstProviderByNpi(npi);

        // Handle all errors first
        if (fetchErrorCode) {
          setApiErrorMessage(VALIDATION_MESSAGES.SEARCH_ERROR);
          onProviderUpdate(null);
          return null;
        }

        if (!foundProvider) {
          // Provider not found
          setApiErrorMessage(VALIDATION_MESSAGES.NPI_NOT_FOUND);
          onProviderUpdate(null);
          return null;
        }

        setApiErrorMessage(null);
        onProviderUpdate(foundProvider);
        return foundProvider;
      } catch (_e) {
        setApiErrorMessage(VALIDATION_MESSAGES.SEARCH_ERROR);
        onProviderUpdate(null);
        return null;
      }
    },
    {
      refreshDeps: [npi, disabled],
      debounceWait: 300,
      ready: shouldSearch,
      onBefore: () => {
        setApiErrorMessage(null);
        onProviderUpdate(null);
      },
      onFinally: () => {
        setLastSearchedNpi(npi ?? '');
        trigger(FIELDS.npi);
      },
    },
  );

  const busy = shouldSearch && (dirty || loading);

  // Only expose error when search is complete and valid
  // (matches Last4Field pattern: only show error when conditions are met)
  const effectiveApiError =
    shouldSearch && !loading && !dirty ? apiErrorMessage : null;

  // Ref allows validation function to read current error without re-registering
  const apiErrorRef = useLatest(effectiveApiError);

  return {
    busy,
    dirty,
    provider: provider || null,
    apiErrorRef,
  };
}

/**
 * A self-contained NPI input field that:
 * - Registers itself with the parent form via `useFormContext`
 * - Validates NPI format (10 digits)
 * - Searches for provider via API with debouncing
 * - Shows loading indicator and clear button
 *
 * @param {Object} props
 * @param {(provider: Provider|null) => void} props.onProviderUpdate - Callback when provider is updated
 * @param {boolean} [props.disabled] - Whether the field is disabled
 */
export default function NpiField({ onProviderUpdate, disabled }) {
  const {
    register,
    errorMessage,
    handleKeyDown,
    currentValue,
    clearField,
    validateField,
  } = useFormField(FIELDS.npi, disabled);

  // Only search if value is a valid NPI format
  const npiToSearch = isValidNpi(currentValue || '') ? currentValue : null;

  // Run async search - calls onProviderUpdate directly when complete (no Effect needed)
  const { busy, dirty, provider, apiErrorRef } = useNpiSearch(
    npiToSearch,
    disabled,
    onProviderUpdate,
  );

  // Register input with validation that checks BOTH format AND API errors
  const { onChange: rhfOnChange, ...restRegistration } = register(FIELDS.npi, {
    validate: (val = '') => {
      if (disabled) return true;
      if (!val?.trim()) return true;
      if (!isValidNpi(val)) return 'NPI must be 10 digits';
      if (apiErrorRef.current) return apiErrorRef.current;
      return true;
    },
  });

  const handleChange = (e) => {
    rhfOnChange(e);
  };

  /**
   * Clear the field and notify parent.
   */
  const handleClearClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearField();
    onProviderUpdate(null);
  };

  /**
   * Trigger validation manually (for search button click).
   */
  const handleValidate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    validateField();
  };

  // Show clear button when we have a provider and not loading
  const showClearButton = provider && !dirty;

  return (
    <FieldWrapper label="NPI" errorMessage={errorMessage}>
      <div className="relative">
        <input
          {...restRegistration}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          type="text"
          inputMode="numeric"
          maxLength={10}
          placeholder="Provider's NPI"
          className={INPUT_WITH_ICON_PADDING_CLASSES}
          aria-invalid={!!errorMessage}
          disabled={disabled}
        />
        {showClearButton ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={handleClearClick}
            className="absolute inset-y-0 right-0 flex items-center pr-3 hover:opacity-70 cursor-pointer"
            title="Clear input"
            disabled={disabled}
          >
            <ClearIcon />
          </button>
        ) : busy ? (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <LoadingIndicator />
          </div>
        ) : (
          <button
            type="button"
            tabIndex={-1}
            onClick={handleValidate}
            className="absolute inset-y-0 right-0 flex items-center pr-3 hover:opacity-70 cursor-pointer"
            title="Validate input"
            disabled={disabled}
          >
            <SearchSubmitIcon className="h-5 w-5 opacity-40" />
          </button>
        )}
      </div>
    </FieldWrapper>
  );
}
